import { useEffect, useRef } from 'react'
import * as signalR from '@microsoft/signalr'
import { jwtDecode } from 'jwt-decode'
import { SignalREventHandler } from './signalREventsHandler.ts'
import type { MessageData } from '../types/signaler.events.types.ts'
import { getEnv } from '../configs/env'
// import { useGlobalContext } from '../context/GlobalContext'

interface JwtPayload {
  PlayerId: string
}

export const useSignalRConnection = (
  promotionId?: number,
  token?: string,
  onShowMessage?: (messageData: MessageData) => void
) => {
//   const { isReadOnlyMode } = useGlobalContext()
const isReadOnlyMode = true; //TODO: change this later

  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const connectionPromiseRef = useRef<Promise<void> | null>(null)
  const connectionKeyRef = useRef<string>('')
  const eventHandlerRef = useRef<SignalREventHandler>(new SignalREventHandler())
  useEffect(() => {
    let isMounted = true

    const setupSignalR = async () => {
      if (!promotionId || !token || !onShowMessage) {
        return
      }

      const connectionKey = `${promotionId}_${token.substring(0, 10)}`
      const hubUrl = `${getEnv().GATEWAY_BASE_URL}/messagingapi/messagingHub?url=Landing-page&promotionId=${promotionId}&isWidget=true${
        isReadOnlyMode ? '&viewAs=false' : ''
      }`

      if (connectionPromiseRef.current && connectionKeyRef.current === connectionKey) {
        try {
          await connectionPromiseRef.current
          return
        } catch (error) {
          console.log('Previous connection attempt failed, starting new one...')
        }
      }

      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        return
      }

      await cleanupExistingConnection()

      const connectionPromise = createAndStartConnection(hubUrl, token, promotionId, isMounted, onShowMessage)

      connectionPromiseRef.current = connectionPromise
      connectionKeyRef.current = connectionKey

      try {
        await connectionPromise
      } catch (error) {
        if (connectionPromiseRef.current === connectionPromise) {
          connectionPromiseRef.current = null
          connectionKeyRef.current = ''
        }
      }
    }

    const cleanupExistingConnection = async () => {
      if (connectionRef.current) {
        try {
          await connectionRef.current.stop()
        } catch (error) {
          console.warn('Error stopping existing connection:', error)
        }
        connectionRef.current = null
      }
    }

    const createAndStartConnection = async (
      hubUrl: string,
      jwtToken: string,
      promotionId: number,
      isMounted: boolean,
      showMessage: (messageData: MessageData) => void
    ): Promise<void> => {
      try {
        const connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => jwtToken,
            withCredentials: false
          })
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
              return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000)
            }
          })
          .configureLogging(signalR.LogLevel.Warning)
          .build()

        connection.on('ReceiveMessage', (sender, message, data) => {
          console.log('📩 SignalR ReceiveMessage:', { sender, message, data })
          eventHandlerRef.current.handleReceiveMessage(message, data, showMessage)
        })

        setupConnectionEventHandlers(connection)

        await connection.start().then(() => {
          console.log('🚀 SignalR connection started successfully', { hubUrl, promotionId })
        })

        if (!isMounted) {
          await connection.stop()
          return
        }

        connectionRef.current = connection

        const decoded = jwtDecode<JwtPayload>(jwtToken)
        await connection.invoke('JoinGroup', `${decoded.PlayerId}_${promotionId}`)
      } catch (error) {
        console.error('SignalR connection error:', error)
        if (isMounted) {
          connectionRef.current = null
        }
        throw error
      }
    }

    const setupConnectionEventHandlers = (connection: signalR.HubConnection) => {
      connection.onreconnecting((error) => {
        console.warn('🔄 SignalR reconnecting...', error)
      })

      connection.onreconnected((connectionId) => {
        console.log('✅ SignalR reconnected with connection ID:', connectionId)
      })

      connection.onclose((error) => {
        console.log('🔌 SignalR connection closed', error)
        connectionRef.current = null
      })
    }

    setupSignalR()
    return () => {
      isMounted = false

      const cleanup = async () => {
        if (connectionRef.current) {
          try {
            await connectionRef.current.stop()
          } catch (error) {
            console.warn('Error stopping SignalR connection:', error)
          }
          connectionRef.current = null
        }

        eventHandlerRef.current.clearProcessedIds()

        if (connectionPromiseRef.current) {
          connectionPromiseRef.current = null
          connectionKeyRef.current = ''
        }
      }

      cleanup()
    }
  }, [promotionId, token, onShowMessage])

  return {
    connection: connectionRef.current,
    isConnected: connectionRef.current?.state === signalR.HubConnectionState.Connected
  }
}
